import React from 'react';
import styled from 'styled-components';
import Humanize from 'humanize-plus';
import { headersTotalWidth } from '~/shared/constants';
import { Tooltip } from '../../Tooltip';
import { font, ChartCell, ChartText } from './sharedStyles';
import { getColumnBorderFlags } from './columnBorderFlags';

const BarCellWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 11px !important;
  padding-right: 0;
  ${font}
`;

const BarWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 5px;
  left: 4px;
  display: flex;
  flex-grow: 1;
  justify-content: flex-start;
  align-items: flex-start;
  ${font}
  color: var(--table-cellrenderer-chart-wrapper-text-color);
`;

const Bar = styled('div')<{ $width: number; $color: string }>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  border-top-right-radius: 30px;
  border-bottom-right-radius: 30px;
  background: ${({ $color }) => $color};
  width: ${({ $width }) => $width}%;
  min-height: 14px;
`;

const PositiveBar = styled('div')<{ $width: number; $color: string; $negativewidth: number }>`
  position: absolute;
  top: 0;
  left: ${({ $negativewidth }) => $negativewidth - 5}%;
  z-index: 1;
  border-top-right-radius: 30px;
  border-bottom-right-radius: 30px;
  background: ${({ $color }) => $color};
  width: ${({ $width }) => $width}%;
  min-height: 14px;
`;

const PositiveBox = styled('div')<{ $width: number; $color: string; $negativewidth: number }>`
  position: absolute;
  top: 0;
  left: ${({ $negativewidth, $width }) => $negativewidth - $width}%;
  z-index: -1;
  background: ${({ $color }) => $color};
  width: ${({ $width }) => $width}%;
  min-height: 14px;
`;

export const StackedhorizontalBarRenderer =
  (
    dataColors: any,
    tableWidth: any,
    hasRowNodes: boolean,
    firstColumnIds: Set<string>,
    lastColumnIds: Set<string>,
    columnIds: string[],
    hasScroll: boolean | undefined,
    indexTitleWidth: number | undefined,
  ) =>
  // eslint-disable-next-line react/display-name
  ({ dataKey, rowData, cellData, columnIndex }: any) => {
    const colors = rowData[`${dataKey}Color`];
    const width =
      (tableWidth / (hasRowNodes ? (indexTitleWidth ? indexTitleWidth + 100 : headersTotalWidth) : 100)) *
      rowData[`${dataKey}Width`] *
      1.2;
    const showText = rowData[`${dataKey}showText`];
    const names = ['Negative', 'Positive'];
    const rawData = rowData?.[`${dataKey}_Components`] ?? rowData?.[dataKey];
    const normalizedValues = Array.isArray(rawData)
      ? rawData
      : typeof rawData === 'string'
        ? rawData.split(',')
        : rawData === undefined || rawData === null
          ? []
          : [rawData];

    const seriesData = normalizedValues.map((value: any, i: number) => {
      const numericValue = typeof value === 'number' ? value : Number(value);
      return {
        data: [Number.isFinite(numericValue) ? numericValue : 0],
        name: names[i],
        color: colors ? colors[i] : dataColors[i],
      };
    });
    const { isFirst, isLast } = getColumnBorderFlags(columnIds, columnIndex, firstColumnIds, lastColumnIds, hasScroll);

    const negativeWidth = (width / (tableWidth / 3)) * seriesData?.[0]?.data?.[0] * (90 / rowData[`${dataKey}Max`]);
    const positiveWidth = (width / (tableWidth / 3)) * seriesData?.[1]?.data?.[0] * (90 / rowData[`${dataKey}Max`]) + 5;

    return (
      <ChartCell $isfirst={isFirst ? 1 : 0} $islast={isLast ? 1 : 0}>
        <BarCellWrapper>
          {showText && <ChartText>{Humanize.toFixed(cellData, 2).replace('.00', '')}</ChartText>}
          <BarWrapper>
            <Tooltip
              arrow
              placement="top"
              html={`${seriesData?.[0]?.name}: ${Humanize.toFixed(seriesData?.[0]?.data?.[0], 2).replace('.00', '')}%`}
            >
              <Bar $width={negativeWidth} $color={colors ? colors[0] : dataColors[0]} />
            </Tooltip>
            {Number(seriesData?.[1]?.data?.[0]) !== 0 && (
              <PositiveBox
                $width={negativeWidth < 5 ? negativeWidth : positiveWidth < 5 ? 5 : 5}
                $negativewidth={negativeWidth}
                $color={colors ? colors[1] : dataColors[1]}
              />
            )}
            <Tooltip
              arrow
              placement="top"
              html={`${seriesData?.[1]?.name}: ${Humanize.toFixed(seriesData?.[1]?.data?.[0], 2).replace('.00', '')}%`}
            >
              <PositiveBar
                $width={positiveWidth}
                $negativewidth={negativeWidth}
                $color={colors ? colors[1] : dataColors[1]}
              />
            </Tooltip>
          </BarWrapper>
        </BarCellWrapper>
      </ChartCell>
    );
  };
